import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Swatches } from "@/components/Swatches";
import { COLLECTIONS, HOW_IT_WORKS, FAQS, SEASON_FACTS, PROMISES, TESTIMONIALS, TIERS } from "@/data/collections";
import { useBooking } from "@/context/BookingContext";

const FOUNDERS_PHOTO = "https://customer-assets-eiarnc6j.emergentagent.net/job_pumpkin-booking/artifacts/nuxx83gs_image.png";
const HERO =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_pumpkin-booking/artifacts/p8rka0nm_image.png";

export default function Home() {
  const nav = useNavigate();
  const { update, reset } = useBooking();

  const choose = (id) => {
    reset();
    update({ collectionId: id });
    nav("/configure");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative h-[78vh] min-h-[520px] overflow-hidden" data-testid="hero-section">
        <img src={HERO} alt="Autumn porch styled with pumpkins" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(43,34,29,0.25), rgba(43,34,29,0.55))" }} />
        <div className="relative h-full pp-container flex flex-col items-center justify-center text-center pp-fade-up">
          <div className="pp-caption mb-5" style={{ color: "#E9D8BE" }} data-testid="hero-caption">Autumn 2026 · A limited number of porches, styled by hand</div>
          <h1
            className="font-serif-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-wide"
            style={{ color: "#F6EEDF" }}
          >
            A porch worth <em className="font-normal">coming home to.</em>
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed" style={{ color: "#EADCC6" }}>
            Heirloom pumpkins, garden mums and gathered autumn pieces — composed for your front step, one porch at a time.
          </p>
          <a href="#collections" data-testid="hero-cta" className="pp-btn mt-10 !rounded-full">
            Check availability <ArrowRight className="h-4 w-4" />
          </a>
          <div className="mt-5 text-xs tracking-wide" style={{ color: "#D9C6A8" }} data-testid="hero-note">Limited spots each week · September through October</div>
        </div>
      </section>

      {/* Collections */}
      <section id="collections" className="pp-grain" style={{ backgroundColor: "var(--pp-parchment)" }}>
        <div className="pp-container py-20 sm:py-28 relative">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <div className="pp-caption mb-3">The collections · Begin here</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light" style={{ color: "var(--pp-burgundy)" }}>
            Choose your style.
          </h2>
          <p className="mt-4 leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>
            Choose the palette that feels like home. You'll shape the details next, pick your window, and pay securely at checkout — your date is booked the moment payment clears.
          </p>
        </div>
        <div className="space-y-6">
          {COLLECTIONS.map((c, i) => (
            <button
              key={c.id}
              data-testid={`style-card-${c.id}`}
              onClick={() => choose(c.id)}
              className="group w-full text-left grid grid-cols-1 md:grid-cols-12 gap-0 overflow-hidden border transition-shadow duration-300 hover:shadow-[0_24px_60px_-30px_rgba(60,35,24,0.45)]"
              style={{ backgroundColor: "var(--pp-cream-card)", borderColor: "var(--pp-border)", animation: `pp-fade-up 0.7s ease-out ${i * 0.08}s both` }}
            >
              <div className={`md:col-span-5 relative aspect-[4/3] md:aspect-auto md:min-h-[300px] overflow-hidden ${i % 2 ? "md:order-2" : ""}`}>
                <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                {c.inspiration && (
                  <span className="absolute bottom-2 right-3 text-[10px] tracking-wider uppercase" style={{ color: "rgba(255,255,255,0.7)", textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>Inspiration</span>
                )}
              </div>
              <div className={`md:col-span-7 p-8 sm:p-12 flex flex-col justify-center ${i % 2 ? "md:order-1" : ""}`}>
                <div className="pp-caption mb-3">Collection {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display text-3xl sm:text-4xl" style={{ color: "var(--pp-burgundy)" }}>{c.name}</h3>
                <div className="mt-2 text-sm tracking-wide" style={{ color: "var(--pp-terracotta)" }} data-testid={`starting-price-${c.id}`}>
                  Starting at ${Math.min(...Object.values(TIERS[c.id]).map((t) => t.price)).toLocaleString()}
                </div>
                <p className="mt-3 max-w-md leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>{c.description}</p>
                <div className="mt-6 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <Swatches colors={c.colors} ring="var(--pp-border)" />
                  <span className="pp-btn-ghost self-start sm:self-auto" style={{ color: "var(--pp-terracotta)" }}>
                    See pricing &amp; details <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-8 text-sm">
          <Link to="/lookup" className="underline underline-offset-4 hover:opacity-70" style={{ color: "var(--pp-brown)" }} data-testid="home-lookup-link">
            Already reserved a date? View your reservation →
          </Link>
        </div>
        </div>
      </section>

      {/* Season & availability */}
      <section className="pp-dark" data-testid="season-section">
        <div className="pp-container py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="pp-caption mb-3" style={{ color: "#C9B8A3" }}>A short season, by design</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight" style={{ color: "var(--pp-parchment)" }}>
              September through November. Then the porch is yours to enjoy.
            </h2>
            <p className="mt-4 leading-relaxed text-sm sm:text-base" style={{ color: "#B9A48C" }}>
              We keep our calendar small on purpose. Two-hour install windows, six days a week, and a limited number of porches each day — so that peak weeks in late September and October are reserved well ahead.
            </p>
          </div>
          <dl className="md:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-6 md:pl-8">
            {SEASON_FACTS.map((f) => (
              <div key={f.label} className="border-t pt-4" style={{ borderColor: "rgba(233,216,190,0.25)" }} data-testid={`season-fact-${f.id}`}>
                <dt className="font-serif-display text-3xl" style={{ color: "#E9D8BE" }}>{f.value}</dt>
                <dd className="mt-1 text-xs tracking-wide uppercase" style={{ color: "#B9A48C" }}>{f.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* How it works */}
      <section className="pp-grain border-t" style={{ backgroundColor: "var(--pp-parchment)", borderColor: "var(--pp-border)" }} data-testid="how-it-works-section">
        <div className="pp-container py-20 sm:py-28 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="pp-caption mb-3">How we work together</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-light" style={{ color: "var(--pp-burgundy)" }}>From first look to front step</h2>
              <p className="mt-4 leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>
                Five unhurried steps. Pay securely at checkout and your window is yours — then we come and style.
              </p>
            </div>
            <ol className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col gap-x-10 gap-y-10">
              {HOW_IT_WORKS.map((s) => (
                <li key={s.n} className="border-t pt-5" style={{ borderColor: "var(--pp-burgundy)" }}>
                  <div className="font-serif-display text-3xl" style={{ color: "var(--pp-terracotta)" }}>{s.n}</div>
                  <h3 className="mt-2 text-lg font-medium" style={{ color: "var(--pp-text)" }}>{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Promises */}
      <section className="pp-container py-16 sm:py-20" data-testid="promises-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROMISES.map((p) => (
            <div key={p.title} className="border p-8" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }}>
              <div className="pp-caption mb-3">{p.caption}</div>
              <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--pp-burgundy)" }}>{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="pp-dark" data-testid="testimonials-section">
        <div className="pp-container py-20 sm:py-28">
          <div className="max-w-2xl">
            <div className="pp-caption mb-3" style={{ color: "var(--pp-terracotta)" }}>Kind words</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight" style={{ color: "var(--pp-parchment)" }} data-testid="testimonials-title">
              From the porches we've styled
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure key={t.name} data-testid={`testimonial-${t.name.toLowerCase()}`}
                className={`border p-8 sm:p-10 flex flex-col ${i === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5"}`}
                style={{ borderColor: "rgba(244,238,227,0.18)", backgroundColor: "rgba(244,238,227,0.04)", animation: `pp-fade-up 0.7s ease-out ${i * 0.12}s both` }}>
                <span className="font-serif-display text-6xl leading-none" style={{ color: "var(--pp-terracotta)" }} aria-hidden="true">&ldquo;</span>
                <blockquote className={`mt-2 leading-relaxed ${t.quote.length < 40 ? "font-serif-display text-3xl" : "text-base sm:text-lg"}`} style={{ color: "var(--pp-parchment)" }}>
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t text-xs tracking-[0.2em] uppercase" style={{ borderColor: "rgba(244,238,227,0.18)", color: "rgba(244,238,227,0.65)" }}>
                  {t.name} &middot; {t.when}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Care teaser */}
      <section className="pp-container py-16 sm:py-20" data-testid="care-teaser">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center border p-8 sm:p-12" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }}>
          <div className="md:col-span-8">
            <div className="pp-caption mb-3">After we've gone</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light" style={{ color: "var(--pp-burgundy)" }}>Keeping your porch at its best</h2>
            <p className="mt-3 max-w-2xl leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>
              A few gentle habits keep the mums blooming and the pumpkins firm through the season — watering, frosty nights, curious squirrels and more.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/care" data-testid="care-teaser-link" className="pp-btn !rounded-full !py-2.5 !px-6 !text-[0.7rem] !font-normal whitespace-nowrap">Read the care guide <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pp-grain border-t" style={{ backgroundColor: "var(--pp-parchment)", borderColor: "var(--pp-border)" }} data-testid="faq-section">
        <div className="pp-container py-20 sm:py-28 relative">
          <h2 className="font-serif-display text-5xl sm:text-6xl text-center font-light" style={{ color: "var(--pp-burgundy)" }}>FAQ's</h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-14">
            {FAQS.map((f) => (
              <div key={f.q}>
                <h3 className="font-serif-display text-3xl leading-tight text-center min-h-[5.5rem] flex items-end justify-center" style={{ color: "var(--pp-burgundy)" }}>{f.q}</h3>
                <div className="my-6 h-px w-full" style={{ backgroundColor: "var(--pp-burgundy)", opacity: 0.35 }} />
                <p className="leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <a href="#collections" data-testid="faq-cta" className="pp-btn !rounded-full">Check availability <ArrowRight className="h-4 w-4" /></a>
          </div>
        </div>
      </section>

      {/* About us */}
      <section id="about" className="border-t" style={{ backgroundColor: "var(--pp-cream)", borderColor: "var(--pp-border)" }} data-testid="about-section">
        <div className="pp-container py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={FOUNDERS_PHOTO} alt="Isabella and Gage, founders of Pumpkin & Porch" data-testid="about-photo" loading="lazy"
                className="w-full aspect-[9/8] object-cover border" style={{ borderColor: "var(--pp-border)" }} />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="pp-caption mb-3">About us</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight" style={{ color: "var(--pp-burgundy)" }} data-testid="about-title">
              Hey! We're Isabella and Gage.
            </h2>
            <div className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: "var(--pp-text-muted)" }} data-testid="about-body">
              <p>We're the founders of Pumpkin &amp; Porch. As college students with a passion for design and business, we started Pumpkin &amp; Porch to make our favorite season more magical and effortless for our neighbors!</p>
              <p>We know how busy life gets, and we believe that coming home to a beautifully styled front porch shouldn't mean spending your weekend dealing with the hassle of hauling pumpkins or cleaning up messy straw. We source high-quality, local produce, design each display by hand, and take care of all the heavy lifting—from delivery to post-holiday haul-away.</p>
              <p className="font-medium" style={{ color: "var(--pp-text)" }}>Thank you for supporting our small business and letting us be a part of your family's fall season!</p>
            </div>
          </div>
        </div>
      </section>

      <div className="flex-1" />
      <Footer />
    </div>
  );
}
